/**
 * In-memory decoded images, keyed by Filestack URL with the signed query string removed.
 *
 * GraphQL re-signs the same handle on every fetch (`?policy=&signature=`). Those params are not
 * part of the asset identity. A new handle (or any other path change) is a different image and
 * misses this cache even if `DocFile.id` stayed the same.
 */
const imageCache = new Map<string, HTMLImageElement>();

export function getOrLoadCanvasImage({
  imageId,
  onError,
  onLoad,
  url,
}: {
  imageId: string;
  onError: (imageId: string) => void;
  onLoad: () => void;
  url: string | undefined;
}): HTMLImageElement | undefined {
  if (!url) return undefined;

  const cacheKey = getCanvasImageCacheKey(url);
  const cachedImage = imageCache.get(cacheKey);
  if (cachedImage) return cachedImage;

  const image = new Image();
  image.src = url;
  imageCache.set(cacheKey, image);
  image.addEventListener('load', onLoad);
  image.addEventListener('error', () => {
    imageCache.delete(cacheKey);
    onError(imageId);
  });
  return image;
}

function getCanvasImageCacheKey(url: string) {
  const queryIndex = url.indexOf('?');
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}
